import type {
  CvVisibilityDefaults,
  ProfileContactVisibility,
  ProfileContactVisibilitySettings,
  ProfilePhotoVisibility,
  ProfilePrivacySettings,
  ProfileVisibility,
  PublicProfileEffectivePrivacy,
} from '@tabbio/types';

export const PROFILE_VISIBILITY_VALUES = ['public', 'employers', 'private'] as const;
export const PROFILE_PHOTO_VISIBILITY_VALUES = ['public', 'employers', 'hidden'] as const;
export const PROFILE_CONTACT_VISIBILITY_VALUES = ['employers', 'hidden'] as const;

export const DEFAULT_CV_VISIBILITY_DEFAULTS: CvVisibilityDefaults = {
  showEmail: true,
  showPhone: true,
  showLocation: true,
  showLinks: true,
  showPhoto: true,
};

export const DEFAULT_PROFILE_PRIVACY_SETTINGS: ProfilePrivacySettings = {
  profileVisibility: 'employers',
  photoVisibility: 'public',
  contactVisibility: {
    email: 'employers',
    phone: 'employers',
  },
  cvVisibilityDefaults: DEFAULT_CV_VISIBILITY_DEFAULTS,
};

export const PRIVACY_POLICY_VERSION = '2026-07-12';

export const DEFAULT_PUBLIC_PROFILE_PRIVACY: PublicProfileEffectivePrivacy = {
  showPhoto: true,
  showEmail: false,
  showPhone: false,
  showLocation: true,
  showLinks: true,
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

export function normalizeProfileVisibility(value: unknown): ProfileVisibility {
  return PROFILE_VISIBILITY_VALUES.includes(value as ProfileVisibility)
    ? (value as ProfileVisibility)
    : DEFAULT_PROFILE_PRIVACY_SETTINGS.profileVisibility;
}

export function normalizeProfilePhotoVisibility(value: unknown): ProfilePhotoVisibility {
  return PROFILE_PHOTO_VISIBILITY_VALUES.includes(value as ProfilePhotoVisibility)
    ? (value as ProfilePhotoVisibility)
    : DEFAULT_PROFILE_PRIVACY_SETTINGS.photoVisibility;
}

export function normalizeProfileContactVisibility(value: unknown): ProfileContactVisibility {
  return PROFILE_CONTACT_VISIBILITY_VALUES.includes(value as ProfileContactVisibility)
    ? (value as ProfileContactVisibility)
    : 'employers';
}

export function deriveCvVisibilityDefaults(
  input: Pick<ProfilePrivacySettings, 'photoVisibility' | 'contactVisibility'>,
): CvVisibilityDefaults {
  return {
    showEmail: input.contactVisibility.email === 'employers',
    showPhone: input.contactVisibility.phone === 'employers',
    showLocation: true,
    showLinks: true,
    showPhoto: input.photoVisibility !== 'hidden',
  };
}

export function normalizeCvVisibilityDefaults(
  value: unknown,
  fallback: CvVisibilityDefaults = DEFAULT_CV_VISIBILITY_DEFAULTS,
): CvVisibilityDefaults {
  const input = isRecord(value) ? value : {};
  return {
    showEmail:
      typeof input.showEmail === 'boolean' ? input.showEmail : fallback.showEmail,
    showPhone:
      typeof input.showPhone === 'boolean' ? input.showPhone : fallback.showPhone,
    showLocation:
      typeof input.showLocation === 'boolean'
        ? input.showLocation
        : fallback.showLocation,
    showLinks:
      typeof input.showLinks === 'boolean' ? input.showLinks : fallback.showLinks,
    showPhoto:
      typeof input.showPhoto === 'boolean' ? input.showPhoto : fallback.showPhoto,
  };
}

export function normalizeProfileContactVisibilitySettings(
  value: unknown,
  fallback: ProfileContactVisibilitySettings = DEFAULT_PROFILE_PRIVACY_SETTINGS.contactVisibility,
): ProfileContactVisibilitySettings {
  const input = isRecord(value) ? value : {};
  return {
    email: normalizeProfileContactVisibility(input.email ?? fallback.email),
    phone: normalizeProfileContactVisibility(input.phone ?? fallback.phone),
  };
}

export function normalizeProfilePrivacySettings(
  value: unknown,
  fallback: ProfilePrivacySettings = DEFAULT_PROFILE_PRIVACY_SETTINGS,
): ProfilePrivacySettings {
  const input = isRecord(value) ? value : {};
  const profileVisibility = normalizeProfileVisibility(
    input.profileVisibility ?? fallback.profileVisibility,
  );
  const photoVisibility = normalizeProfilePhotoVisibility(
    input.photoVisibility ?? fallback.photoVisibility,
  );
  const contactVisibility = normalizeProfileContactVisibilitySettings(
    input.contactVisibility ?? fallback.contactVisibility,
    fallback.contactVisibility,
  );
  const derivedVisibility = deriveCvVisibilityDefaults({
    photoVisibility,
    contactVisibility,
  });
  const cvVisibilityDefaults = normalizeCvVisibilityDefaults(
    input.cvVisibilityDefaults,
    {
      ...derivedVisibility,
      showLocation: fallback.cvVisibilityDefaults.showLocation,
      showLinks: fallback.cvVisibilityDefaults.showLinks,
    },
  );

  return {
    profileVisibility,
    photoVisibility,
    contactVisibility,
    cvVisibilityDefaults: {
      ...cvVisibilityDefaults,
      showEmail:
        contactVisibility.email === 'hidden' ? false : cvVisibilityDefaults.showEmail,
      showPhone:
        contactVisibility.phone === 'hidden' ? false : cvVisibilityDefaults.showPhone,
      showPhoto:
        photoVisibility === 'hidden' ? false : cvVisibilityDefaults.showPhoto,
    },
  };
}

export function getProfileVisibilityPersistence(
  privacy: ProfilePrivacySettings,
) {
  return {
    visibility: privacy.profileVisibility,
    discoverable: privacy.profileVisibility !== 'private',
    hideFromSearchEngines: privacy.profileVisibility !== 'public',
  };
}

export function getPublicProfileEffectivePrivacy(
  privacy: ProfilePrivacySettings,
): PublicProfileEffectivePrivacy {
  return {
    showPhoto:
      privacy.profileVisibility === 'public' &&
      privacy.photoVisibility === 'public',
    showEmail: false,
    showPhone: false,
    showLocation: privacy.cvVisibilityDefaults.showLocation,
    showLinks: privacy.cvVisibilityDefaults.showLinks,
  };
}

export function getPrivateCvVisibilityDefaults(
  privacy: ProfilePrivacySettings,
): CvVisibilityDefaults {
  return {
    ...privacy.cvVisibilityDefaults,
    showEmail: privacy.contactVisibility.email === 'employers',
    showPhone: privacy.contactVisibility.phone === 'employers',
    // `photoVisibility: 'hidden'` means "remove the real image and use a neutral
    // placeholder" (per the settings-sheet copy), so it must also drop the photo
    // from the user's *own* export: otherwise a hidden photo still ships in the
    // downloaded CV (issue #81). The CV "Show photo" design toggle
    // (cvConfig.showPhoto, enforced server-side) narrows it further; this only
    // sets the default. `profileVisibility` is intentionally NOT consulted here -
    // that gates public surfaces via getPublicProfileEffectivePrivacy, not the
    // owner's private download.
    showPhoto: privacy.photoVisibility !== 'hidden',
  };
}
